import React, { FC, ReactNode, useEffect, useState } from 'react';
import s from '#components/sections/hero-unit/heroUnit.module.scss';
import { useWindowDimensions } from '#hooks';
import HeroMeta from '#components/common/heroMeta/HeroMeta';
import HeroTeaserTitle from '#components/teasers/hero-teaser/HeroTeaserTitle';
import Typography from '#components/typography/Typography';
import LivestreamStateDisplay from '#components/feedback/livestream-state/LivestreamState';
import {
  Category,
  ImageFormat,
  LivestreamDetails,
} from '#types/generated/graphql-types';
import Link from 'next/link';
import Mediabox from '#components/common/mediabox/Mediabox';
import { VIDEO_FORMAT_360 } from '#config/constants';

type Props = {
  children?: ReactNode;
  backgroundImageFormats?: ImageFormat;
  brandName?: string;
  brandLogoImage?: string;
  categories?: Category[];
  description?: string;
  fskRating?: string;
  mediaDuration?: number;
  mediaQuality?: string;
  seasons?: unknown[];
  studioName?: string;
  studioLogoImage?: string;
  studioUrl?: string;
  showBackground?: boolean;
  subtitle?: string;
  title: string;
  titleImage?: string;
  year?: number;
  buttonComponent?: JSX.Element;
  streamDetails?: LivestreamDetails;
  tags?: string[];
};

const HeroUnit: FC<Props> = ({
  backgroundImageFormats,
  brandName,
  brandLogoImage,
  buttonComponent,
  categories,
  description,
  fskRating,
  mediaDuration,
  mediaQuality,
  studioLogoImage,
  studioName,
  studioUrl,
  children,
  showBackground = true,
  seasons,
  streamDetails,
  subtitle,
  title,
  titleImage,
  year,
  tags,
}) => {
  const { dimensions } = useWindowDimensions();
  const [backgroundImage, setBackgroundImage] = useState(null);
  const is360Video = Array.isArray(tags) && tags.includes(VIDEO_FORMAT_360);

  useEffect(() => {
    if (backgroundImageFormats) {
      if (dimensions.width <= 640) {
        setBackgroundImage({
          srcSet: [backgroundImageFormats?.threeByFive?.medium],
          alt: title,
        });
      } else {
        setBackgroundImage({
          srcSet: [backgroundImageFormats?.sixteenByNine?.large],
          alt: title,
        });
      }
    }
  }, [dimensions, backgroundImageFormats]);

  return (
    <section className={s.host}>
      {showBackground && (
        <div
          className={`tw-absolute tw-inset-0 tw-overflow-hidden ${s.background}`}
        >
          <img
            className="tw-object-cover tw-w-full tw-h-full"
            alt={backgroundImage?.alt}
            srcSet={backgroundImage?.srcSet?.join(',')}
          />
        </div>
      )}

      {children}

      <div className={s.inner}>
        <div className={s.textbox}>
          {streamDetails?.streamState && (
            <LivestreamStateDisplay
              streamState={streamDetails.streamState}
              classes="tw-text-xlarge tw-inline-block"
            />
          )}

          <header>
            <HeroTeaserTitle title={title} titleImage={titleImage} />
            {!!subtitle && (
              <Typography
                fontWeight="black"
                classes="tw-mb-4 tw-line-clamp-2 small:tw-text-h4 medium:tw-text-h3 large-up:tw-text-h2"
              >
                {subtitle}
              </Typography>
            )}
          </header>
          {!!description && (
            // eslint-disable-next-line react/no-danger
            <Typography
              classes="tw-line-clamp-4 large:tw-line-clamp-3 medium:tw-line-clamp-2 tw-font-bold tw-mb-4 small:tw-hidden"
              dangerouslySetInnerHTML={{
                __html: description,
              }}
            ></Typography>
          )}
          <HeroMeta
            duration={mediaDuration}
            fromAge={fskRating}
            genres={categories}
            seasons={seasons}
            streamDetails={streamDetails}
            year={year}
            mediaQuality={mediaQuality}
            is360Video={is360Video}
            classes="tw-mb-4 medium-up:tw-mb-8"
          />
          {buttonComponent}
        </div>

        <div className="tw-flex tw-gap-4 tw-mb-4 tw-items-end large-up:tw-mb-0 large-up:tw-flex-col large-up:tw-items-end">
          {brandLogoImage && (
            <div className="tw-flex large-up:tw-items-center large-up:tw-grow">
              <img
                alt={brandName}
                src={brandLogoImage}
                title={brandName}
                className="tw-max-h-20 large-up:tw-max-h-[18.75rem]"
              />
            </div>
          )}

          {studioLogoImage && (
            <div className="tw-w-[4.8rem] tw-h-[4.8rem]">
              {!!studioUrl && (
                <Link href={studioUrl} passHref={true}>
                  <a>
                    <Mediabox
                      aspectRatio="1_1"
                      alt={studioName}
                      src={studioLogoImage}
                      isBackgroundTransparent
                    />
                  </a>
                </Link>
              )}
              {!studioUrl && (
                <Mediabox
                  aspectRatio="1_1"
                  alt={studioName}
                  src={studioLogoImage}
                  isBackgroundTransparent
                />
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default HeroUnit;
